import React, { useState } from 'react';
import { toast } from 'sonner';
import { CopyIcon, FileSpreadsheetIcon, FlaskConicalIcon, LockIcon, PencilLineIcon, ShieldCheckIcon } from 'lucide-react';
import { DASHBOARD_PIN_NOTE, changeSummaryV2, employeeSettings, initialAuditLog, planVersions, validationByVersion } from '../data/compPlan';
import { OpenQuestionBadge } from '../components/sf/OpenQuestionBadge';
import { currentUser, ruleEditorPermission, ruleEditors } from '../data/people';
import { Badge } from '../components/sf/Badge';
import { Button } from '../components/sf/Button';
import { NewBadge } from '../components/sf/NewBadge';
import { HighlightField, RecordHeader } from '../components/sf/RecordHeader';
import { ScopedNotification } from '../components/sf/ScopedNotification';
import { SelectField } from '../components/sf/SelectField';
import { StatusBadge } from '../components/sf/StatusBadge';
import { Tabs } from '../components/sf/Tabs';
import { Tooltip } from '../components/sf/Tooltip';
import { AuditLogTable } from '../components/compPlan/AuditLogTable';
import { ChangeSummaryCard } from '../components/compPlan/ChangeSummaryCard';
import { EmployeeRulesCard } from '../components/compPlan/EmployeeRulesCard';
import { LoadDefaultsModal } from '../components/compPlan/LoadDefaultsModal';
import { PrecedenceCard } from '../components/compPlan/PrecedenceCard';
import { RulesTable } from '../components/compPlan/RulesTable';
import { ValidationCard } from '../components/compPlan/ValidationCard';
import { VersionHistoryTable } from '../components/compPlan/VersionHistoryTable';
import { AuditEntry, EmployeeSetting } from '../types/commission';
type PlanTab = 'rules' | 'audit' | 'history';
interface CompPlanAdminProps {
  canEditRules: boolean;
}
export function CompPlanAdmin({
  canEditRules
}: CompPlanAdminProps) {
  const [versionNumber, setVersionNumber] = useState(1);
  const [tab, setTab] = useState<PlanTab>('rules');
  const [auditLog, setAuditLog] = useState<AuditEntry[]>(initialAuditLog);
  const [isLoadOpen, setIsLoadOpen] = useState(false);
  const version = planVersions.find(item => item.version === versionNumber) ?? planVersions[0];
  const isDraft = version.status === 'Draft';
  const editable = isDraft && canEditRules;
  const viewer = canEditRules ? currentUser : 'Read-only user';
  const editorsLabel = ruleEditors.join(' and ');
  const readOnlyReason = !canEditRules ? `Only ${editorsLabel} (${ruleEditorPermission}) can edit rules.` : 'Active versions are read-only. Clone to a new version to change rates.';
  const fields: HighlightField[] = [{
    label: 'Version',
    value: version.label
  }, {
    label: 'Effective Start',
    value: version.effectiveStartLabel
  }, {
    label: 'Effective End',
    value: version.effectiveEndLabel
  }, {
    label: 'Rules',
    value: String(version.rules.length)
  }, {
    label: 'Rule Editors',
    value: `${ruleEditors.join(', ')}`
  }, {
    label: 'Permission',
    value: ruleEditorPermission
  }];
  const addAudit = (entries: AuditEntry[]) => setAuditLog(prev => [...entries, ...prev]);
  const handleToggleBonus = (setting: EmployeeSetting, bonusOn: boolean) => {
    addAudit([{
      id: `audit-${Date.now()}-${setting.id}`,
      when: 'Just now',
      who: viewer,
      ruleKey: `V${version.version}-EMP-${setting.name.split(' ')[1]?.toUpperCase() ?? setting.id}`,
      field: 'Monthly Bonus switch',
      from: bonusOn ? 'Off' : 'On',
      to: bonusOn ? 'On' : 'Off',
      reason: 'Employee rule toggled in Draft'
    }]);
    toast.success(`Monthly Bonus turned ${bonusOn ? 'on' : 'off'} for ${setting.name}`, {
      description: 'Logged to the audit log.'
    });
  };
  const handleClone = () => toast.success(`Cloned ${version.label} to v3 Draft`, {
    description: 'All rules copied with new V3 rule keys. Set an effective period to continue.'
  });
  const handleLoad = (includeSampleData: boolean) => {
    setIsLoadOpen(false);
    toast.success('Default rules loaded from 2026-v1-parity', {
      description: includeSampleData ? 'Sample LOs, loans and an October 2026 period were added.' : 'Rules only — no sample data.'
    });
  };
  return <div className="space-y-3" data-id="element-1108" data-mp="2cutunr35tmxo">
      <RecordHeader objectLabel="Comp Plan" title="LO Standard Plan" icon={FileSpreadsheetIcon} iconBg="#F49756" titleAddon={<>
            <SelectField id="plan-version" label="Plan version" hideLabel value={String(versionNumber)} options={planVersions.map(item => ({
        value: String(item.version),
        label: `${item.label} · ${item.status}`
      }))} onChange={value => setVersionNumber(Number(value))} data-id="element-1110" data-mp="2lw0f2b8xjhw7" />
            <StatusBadge status={version.status} data-id="element-1111" data-mp="35j7u8b9wcrbc" />
            <NewBadge label="NEW · plan versions & effective dates" data-id="element-1112" data-mp="35kiwu7rlvt0v" />
          </>} actions={<>
            <span className="inline-flex items-center gap-1 rounded-full border border-[#F9C88C] bg-sf-amber-light px-2 py-0.5 text-[11px] font-semibold text-sf-amber-text" data-id="element-1113" data-mp="39l2x3k7h7cr8">
              <FlaskConicalIcon className="h-3 w-3" aria-hidden="true" data-id="element-1114" data-mp="tv31ax88te2v" />
              Sandbox
            </span>
            <NewBadge label="NEW" data-id="element-1115" data-mp="oxb5vt8e39tx" />
            {canEditRules ? <Button onClick={() => setIsLoadOpen(true)} data-id="element-1116" data-mp="jyhvwasba0yi">Load Default Rules</Button> : <Tooltip content={readOnlyReason} side="bottom" data-id="element-1117" data-mp="1hkf5tjsmkxyu">
                <Button disabled data-id="element-1118" data-mp="1cmdlkvjgqsls">Load Default Rules</Button>
              </Tooltip>}
            <Tooltip content={isDraft ? 'Effective period is TBD. Set a start date before activating.' : `${version.label} is already the Active version.`} side="bottom" data-id="element-1119" data-mp="17vnjpsa8x7vu">
              <Button disabled data-id="element-1120" data-mp="20boj845io6vp">Activate</Button>
            </Tooltip>
            {canEditRules ? <Button variant="brand" icon={<CopyIcon data-id="element-1122" data-mp="1vk6ovyy014jp" />} onClick={handleClone} data-id="element-1121" data-mp="1vlhrhvfpk698">
                Clone to New Version
              </Button> : <Tooltip content={readOnlyReason} side="bottom" align="end" data-id="element-1123" data-mp="3ptvcmdqaykle">
                <Button variant="brand" icon={<CopyIcon data-id="element-1125" data-mp="3twrpdnt5vmhf" />} disabled data-id="element-1124" data-mp="3ttdqipjphveq">
                  Clone to New Version
                </Button>
              </Tooltip>}
          </>} fields={fields} data-id="element-1109" data-mp="2ctiqhq5syd61" />

      <div className="grid grid-cols-1 items-start gap-3 lg:grid-cols-[minmax(0,1fr)_320px]" data-id="element-1126" data-mp="3l0ipk819ulur">
        <div className="space-y-3" data-id="element-1127" data-mp="363rrc23c3g6q">
          <section className="rounded border border-sf-border bg-white shadow-card" data-id="element-1128" data-mp="3b3xnnvkjtqv5">
            <div className="flex items-center justify-between border-b border-sf-border pr-4" data-id="element-1129" data-mp="2x4eer0jwybhs">
              <div className="flex-1 [&>div]:border-b-0" data-id="element-1130" data-mp="2x6r6u6jdy28k">
                <Tabs label="Plan sections" tabs={[{
                id: 'rules',
                label: `Comp Rules (${version.rules.length})`
              }, {
                id: 'audit',
                label: `Audit Log (${auditLog.length})`
              }, {
                id: 'history',
                label: 'Version History'
              }]} active={tab} onChange={setTab} data-id="element-1131" data-mp="324akp5c68czz" />
              </div>
              <Badge tone={canEditRules ? 'successLight' : 'neutral'} icon={canEditRules ? <ShieldCheckIcon className="h-3 w-3" aria-hidden="true" data-id="element-1133" data-mp="1j9o86xm1w3s5" /> : <LockIcon className="h-3 w-3" aria-hidden="true" data-id="element-1134" data-mp="1ehei9hkxr3pv" />} data-id="element-1132" data-mp="15f5dz9g9g6kx">
                {canEditRules ? `${viewer} · ${ruleEditorPermission}` : `${viewer} · Read-only`}
              </Badge>
            </div>
            <div className="space-y-3 p-4" data-id="element-1135" data-mp="1o6eljd6h0l9v">
              {tab === 'rules' && <>
                  {!canEditRules ? <ScopedNotification tone="info" icon={LockIcon} data-id="element-1136" data-mp="2254srln081by">
                      Read-only. Only {editorsLabel} ({ruleEditorPermission}) can edit commission rules.
                    </ScopedNotification> : isDraft ? <ScopedNotification tone="warning" icon={PencilLineIcon} data-id="element-1137" data-mp="3sebuuvh18lw">
                      <strong data-id="element-1138" data-mp="scsxdaxkcg4m">Draft — editable.</strong> Click any Min, Max or Value to edit. Saving requires a reason. Effective period: TBD.
                    </ScopedNotification> : <ScopedNotification tone="info" icon={LockIcon} action={<Button size="sm" onClick={handleClone} data-id="element-1140" data-mp="163fuwzrl7zz4">
                          Clone to New Version
                        </Button>} data-id="element-1139" data-mp="121uej4fuh3k6">
                      Active versions can’t be edited. Clone to a new version to change rates.
                    </ScopedNotification>}
                  <RulesTable key={version.version} version={version} editable={editable} readOnlyReason={readOnlyReason} editor={viewer} onAudit={addAudit} data-id="element-1141" data-mp="nd81589s2lvl" />
                </>}
              {tab === 'audit' && <AuditLogTable entries={auditLog} data-id="element-1142" data-mp="1ys3mte6udnln" />}
              {tab === 'history' && <VersionHistoryTable versions={planVersions} data-id="element-1143" data-mp="28wk3uaw25ar1" />}
            </div>
          </section>
          {tab === 'rules' && <EmployeeRulesCard key={version.version} settings={employeeSettings} editable={editable} onToggleBonus={handleToggleBonus} data-id="element-1144" data-mp="23xqla41wytk0" />}
        </div>

        <aside className="space-y-3" aria-label="Plan checks" data-id="element-1145" data-mp="1z4xk1q5nm3co">
          <ValidationCard title="Validation" checks={validationByVersion[version.version] ?? []} data-id="element-1146" data-mp="1v5ygo9zkmenc" />
          {isDraft && <ChangeSummaryCard changes={changeSummaryV2} data-id="element-1147" data-mp="1q73myd02f5og" />}
          <PrecedenceCard data-id="element-1148" data-mp="1l6z80ret2j0c" />
          <p className="flex flex-wrap items-center gap-1.5 rounded border border-sf-border bg-white px-3 py-2 text-xs text-sf-weak shadow-card" data-id="element-1149" data-mp="1l6pq7r3wzi5q">
            <LockIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1150" data-mp="1g9z16ea4tc86" />
            {DASHBOARD_PIN_NOTE}
            <OpenQuestionBadge data-id="element-1151" data-mp="1c8du59ka3s5w" />
          </p>
        </aside>
      </div>

      <LoadDefaultsModal open={isLoadOpen} onClose={() => setIsLoadOpen(false)} onLoad={handleLoad} data-id="element-1152" data-mp="3akm4a9riwo9s" />
    </div>;
}